/**
 * OrgBadge — a tiny monochrome chip naming which ORG an Inbox row belongs to.
 *
 * The Inbox is the user's PERSONAL, GLOBAL view (founder 2026-07-05): its rows
 * span every org the user can see, so each row needs to say WHICH org it is
 * from. This badge sits at the head of a row's breadcrumb line, before the
 * Project · Board crumb, so cross-org items are instantly tellable apart.
 *
 * ADR-057: monochrome — semantic tokens only (no raw hex), a subtle bordered
 * chip with the Building2 org glyph + the org display name. The name is resolved
 * from the orgs list the org switcher already loads (useOrgLabel); if it can't
 * be resolved yet (list still loading / unknown org) the badge renders nothing
 * rather than a broken placeholder. English-only.
 */
import { Building2 } from 'lucide-react';
import type * as React from 'react';

import { cn } from '../../lib/utils';
import { useOrgLabel } from './inbox-shared';

interface OrgBadgeProps {
  orgId: string | null | undefined;
  className?: string;
}

export function OrgBadge({ orgId, className }: OrgBadgeProps): React.ReactElement | null {
  const orgName = useOrgLabel(orgId);
  if (!orgName) return null;
  return (
    <span
      className={cn(
        'inline-flex max-w-[45%] shrink-0 items-center gap-1 rounded border border-border/70',
        'px-1.5 py-px text-[10px] font-medium leading-tight text-muted-foreground',
        className,
      )}
      title={orgName}
      data-testid="inbox-org-badge"
    >
      <Building2 className="h-2.5 w-2.5 shrink-0 opacity-70" aria-hidden />
      <span className="truncate">{orgName}</span>
    </span>
  );
}
